// src/utils/printUtils.js
//
// All print logic for On Purpose Planner.
// Opens a clean black-and-white print window for each section.

import { db } from './firebaseConfig';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { format } from 'date-fns';

// ─── Shared print stylesheet ──────────────────────────────────────────────────
const PRINT_STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 12pt;
    color: #000;
    background: #fff;
    padding: 0.75in;
    line-height: 1.5;
  }

  h1 {
    font-size: 20pt;
    font-weight: bold;
    margin-bottom: 4pt;
    letter-spacing: -0.01em;
  }

  h2 {
    font-size: 15pt;
    font-weight: bold;
    margin-bottom: 6pt;
    margin-top: 14pt;
  }

  h3 {
    font-size: 12pt;
    font-weight: bold;
    margin-bottom: 4pt;
    margin-top: 10pt;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  p, li {
    font-size: 11pt;
    margin-bottom: 3pt;
  }

  .header {
    border-bottom: 2px solid #000;
    padding-bottom: 8pt;
    margin-bottom: 16pt;
  }

  .subtitle {
    font-size: 10pt;
    color: #555;
    margin-top: 2pt;
  }

  /* Journal entries */
  .journal-entry {
    margin-bottom: 16pt;
  }

  .journal-date {
    font-size: 11pt;
    font-weight: bold;
    letter-spacing: 0.02em;
    margin-bottom: 4pt;
  }

  .journal-body {
    font-size: 11pt;
    white-space: pre-wrap;
    line-height: 1.65;
    padding-left: 8pt;
  }

  .entry-divider {
    border: none;
    border-top: 1px solid #999;
    margin: 12pt 0;
  }

  /* Task categories */
  .category-block {
    margin-bottom: 14pt;
  }

  .category-label {
    font-size: 11pt;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    border-bottom: 1px solid #ccc;
    padding-bottom: 3pt;
    margin-bottom: 6pt;
  }

  .task-row {
    display: flex;
    align-items: flex-start;
    gap: 8pt;
    padding: 3pt 0;
    border-bottom: 1px dotted #ddd;
  }

  .task-checkbox {
    width: 11pt;
    height: 11pt;
    border: 1.5px solid #333;
    border-radius: 2px;
    flex-shrink: 0;
    margin-top: 1pt;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 8pt;
  }

  .task-complete .task-checkbox::after {
    content: '✓';
    font-weight: bold;
  }

  .task-title {
    font-size: 11pt;
    flex: 1;
  }

  .task-complete .task-title {
    text-decoration: line-through;
    color: #666;
  }

  .task-status {
    font-size: 9pt;
    color: #555;
    flex-shrink: 0;
    font-style: italic;
  }

  .task-notes {
    font-size: 9.5pt;
    color: #444;
    padding-left: 19pt;
    padding-bottom: 4pt;
    font-style: italic;
    white-space: pre-wrap;
  }

  /* Goals */
  .goal-row {
    display: flex;
    align-items: flex-start;
    gap: 8pt;
    padding: 3pt 0;
    border-bottom: 1px dotted #ddd;
  }

  .goal-checkbox {
    width: 11pt;
    height: 11pt;
    border: 1.5px solid #333;
    border-radius: 2px;
    flex-shrink: 0;
    margin-top: 1pt;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 8pt;
  }

  .goal-complete .goal-checkbox::after {
    content: '✓';
    font-weight: bold;
  }

  .goal-title {
    font-size: 11pt;
    flex: 1;
  }

  .goal-complete .goal-title {
    text-decoration: line-through;
    color: #666;
  }

  /* Roles & Routines */
  .role-card {
    margin-bottom: 18pt;
    border-top: 2px solid #333;
    padding-top: 8pt;
  }

  .role-title {
    font-size: 13pt;
    font-weight: bold;
    margin-bottom: 4pt;
  }

  .role-description {
    font-size: 10.5pt;
    font-style: italic;
    color: #444;
    margin-bottom: 8pt;
  }

  .bands-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10pt;
    margin-top: 6pt;
  }

  .band-label {
    font-size: 9pt;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    border-bottom: 1px solid #ccc;
    padding-bottom: 2pt;
    margin-bottom: 4pt;
  }

  .band-text {
    font-size: 10pt;
    white-space: pre-wrap;
    line-height: 1.55;
    color: #222;
  }

  /* Notes/Calendar fallback */
  .section-content {
    font-size: 11pt;
    white-space: pre-wrap;
    line-height: 1.65;
  }

  @media print {
    body { padding: 0.5in; }
    @page { margin: 0.5in; }
  }
`;

// ─── Helper: open print window ────────────────────────────────────────────────
function openPrintWindow(title, bodyHtml) {
  const win = window.open('', '_blank', 'width=800,height=600');
  if (!win) {
    alert('Pop-up blocked. Please allow pop-ups for this site to print.');
    return;
  }
  win.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${title}</title>
        <style>${PRINT_STYLES}</style>
      </head>
      <body>
        ${bodyHtml}
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() { window.close(); };
          };
        <\/script>
      </body>
    </html>
  `);
  win.document.close();
}

// ─── Helper: escape HTML ──────────────────────────────────────────────────────
function esc(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ─── Helper: local date string (timezone-safe) ────────────────────────────────
function localDateStr(date) {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// PRINT: My Day (Tasks)
// ─────────────────────────────────────────────────────────────────────────────
export async function printTasks(user, currentDate) {
  const dateStr   = format(currentDate, 'yyyy-MM-dd');
  const dateLabel = format(currentDate, 'EEEE, MMMM d, yyyy');

  // Fetch tasks for this date
  const q        = query(
    collection(db, 'tasks'),
    where('userId', '==', user.uid),
    where('date', '==', dateStr)
  );
  const snapshot = await getDocs(q);
  const tasks    = snapshot.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999));

  const categories = [
    { key: 'must-do',   label: 'Must Do' },
    { key: 'should-do', label: 'Should Do' },
    { key: 'could-do',  label: 'Could Do' },
  ];

  const categorySections = categories.map(({ key, label }) => {
    const catTasks = tasks.filter(t => t.category === key);

    // Sort: incomplete first, then complete
    catTasks.sort((a, b) => {
      const aC = a.status === 'complete' ? 1 : 0;
      const bC = b.status === 'complete' ? 1 : 0;
      if (aC !== bC) return aC - bC;
      return (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999);
    });

    const taskRows = catTasks.map(task => {
      const isComplete  = task.status === 'complete';
      const statusLabel = task.status !== 'pending' && task.status !== 'complete'
        ? task.status.charAt(0).toUpperCase() + task.status.slice(1)
        : '';

      const notesRow = task.notes
        ? `<div class="task-notes">${esc(task.notes)}</div>`
        : '';

      return `
        <div class="task-row ${isComplete ? 'task-complete' : ''}">
          <div class="task-checkbox"></div>
          <span class="task-title">${esc(task.title)}</span>
          ${statusLabel ? `<span class="task-status">${esc(statusLabel)}</span>` : ''}
        </div>
        ${notesRow}
      `;
    }).join('');

    const emptyNote = catTasks.length === 0
      ? `<p style="color:#999;font-style:italic;font-size:10pt;padding:2pt 0;">No tasks</p>`
      : '';

    return `
      <div class="category-block">
        <div class="category-label">${esc(label)}</div>
        ${taskRows}
        ${emptyNote}
      </div>
    `;
  }).join('');

  const html = `
    <div class="header">
      <h1>My Day</h1>
      <p class="subtitle">${esc(dateLabel)}</p>
    </div>
    ${categorySections}
    <p style="margin-top:18pt;font-size:9pt;color:#888;">Printed from On Purpose Planner &mdash; onpurposeplanner.com</p>
  `;

  openPrintWindow(`My Day – ${dateLabel}`, html);
}

// ─────────────────────────────────────────────────────────────────────────────
// PRINT: Journal — today only
// ─────────────────────────────────────────────────────────────────────────────
export async function printJournalToday(user, currentDate) {
  const dateStr   = format(currentDate, 'yyyy-MM-dd');
  const dateLabel = format(currentDate, 'EEEE, MMMM d, yyyy');

  const q        = query(
    collection(db, 'notes'),
    where('userId', '==', user.uid),
    where('date', '==', dateStr)
  );
  const snapshot = await getDocs(q);
  const content  = snapshot.docs.length > 0
    ? (snapshot.docs[0].data().content || '')
    : '';

  const entryHtml = content.trim()
    ? `<div class="journal-body">${esc(content)}</div>`
    : `<p style="color:#999;font-style:italic;">No entry for this date.</p>`;

  const html = `
    <div class="header">
      <h1>Journal</h1>
      <p class="subtitle">${esc(dateLabel)}</p>
    </div>
    <div class="journal-entry">
      ${entryHtml}
    </div>
    <p style="margin-top:18pt;font-size:9pt;color:#888;">Printed from On Purpose Planner &mdash; onpurposeplanner.com</p>
  `;

  openPrintWindow(`Journal – ${dateLabel}`, html);
}

// ─────────────────────────────────────────────────────────────────────────────
// PRINT: Journal — date range (continuous feed, skip empty days)
// ─────────────────────────────────────────────────────────────────────────────
export async function printJournalRange(user, startDateStr, endDateStr) {
  // Fetch all notes in range with a JS-side filter (avoids composite index)
  const q        = query(
    collection(db, 'notes'),
    where('userId', '==', user.uid)
  );
  const snapshot = await getDocs(q);

  // Build a map of dateStr → content
  const entryMap = {};
  snapshot.docs.forEach(d => {
    const data = d.data();
    if (data.date >= startDateStr && data.date <= endDateStr && data.content?.trim()) {
      entryMap[data.date] = data.content;
    }
  });

  // Walk the date range in order
  const entries = [];
  let cursor    = new Date(startDateStr + 'T12:00:00'); // noon avoids DST edge
  const endDate = new Date(endDateStr   + 'T12:00:00');

  while (cursor <= endDate) {
    const key = localDateStr(cursor);
    if (entryMap[key]) {
      entries.push({ dateStr: key, content: entryMap[key] });
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  if (entries.length === 0) {
    alert('No journal entries found in that date range.');
    return;
  }

  const startLabel = format(new Date(startDateStr + 'T12:00:00'), 'MMMM d, yyyy');
  const endLabel   = format(new Date(endDateStr   + 'T12:00:00'), 'MMMM d, yyyy');

  const entriesHtml = entries.map((entry, index) => {
    const label   = format(new Date(entry.dateStr + 'T12:00:00'), 'EEEE, MMMM d, yyyy');
    const divider = index > 0 ? `<hr class="entry-divider" />` : '';
    return `
      ${divider}
      <div class="journal-entry">
        <div class="journal-date">${esc(label)}</div>
        <div class="journal-body">${esc(entry.content)}</div>
      </div>
    `;
  }).join('');

  const html = `
    <div class="header">
      <h1>Journal</h1>
      <p class="subtitle">${esc(startLabel)} &ndash; ${esc(endLabel)} &nbsp;&middot;&nbsp; ${entries.length} entr${entries.length === 1 ? 'y' : 'ies'}</p>
    </div>
    ${entriesHtml}
    <p style="margin-top:18pt;font-size:9pt;color:#888;">Printed from On Purpose Planner &mdash; onpurposeplanner.com</p>
  `;

  openPrintWindow(`Journal – ${startLabel} to ${endLabel}`, html);
}

// ─────────────────────────────────────────────────────────────────────────────
// PRINT: Goals
// ─────────────────────────────────────────────────────────────────────────────
export async function printGoals(user, selectedYear, selectedMonth) {
  const monthNames = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];

  // Fetch yearly goals
  const yearlyQ = query(
    collection(db, 'goals'),
    where('userId', '==', user.uid),
    where('type', '==', 'yearly'),
    where('year', '==', selectedYear)
  );
  const yearlySnap  = await getDocs(yearlyQ);
  const yearlyGoals = yearlySnap.docs.map(d => ({ id: d.id, ...d.data() }));

  // Fetch monthly goals
  const monthlyQ = query(
    collection(db, 'goals'),
    where('userId', '==', user.uid),
    where('type', '==', 'monthly'),
    where('year', '==', selectedYear),
    where('month', '==', selectedMonth)
  );
  const monthlySnap  = await getDocs(monthlyQ);
  const monthlyGoals = monthlySnap.docs.map(d => ({ id: d.id, ...d.data() }));

  const renderGoals = (goals) => {
    if (goals.length === 0) {
      return `<p style="color:#999;font-style:italic;font-size:10pt;">No goals recorded.</p>`;
    }
    return goals.map(g => `
      <div class="goal-row ${g.completed ? 'goal-complete' : ''}">
        <div class="goal-checkbox"></div>
        <span class="goal-title">${esc(g.title)}</span>
      </div>
    `).join('');
  };

  const html = `
    <div class="header">
      <h1>Goals</h1>
      <p class="subtitle">${esc(String(selectedYear))} &nbsp;&middot;&nbsp; ${esc(monthNames[selectedMonth - 1])}</p>
    </div>

    <div class="category-block">
      <div class="category-label">Yearly Goals &mdash; ${esc(String(selectedYear))}</div>
      ${renderGoals(yearlyGoals)}
    </div>

    <div class="category-block">
      <div class="category-label">Monthly Goals &mdash; ${esc(monthNames[selectedMonth - 1])} ${esc(String(selectedYear))}</div>
      ${renderGoals(monthlyGoals)}
    </div>

    <p style="margin-top:18pt;font-size:9pt;color:#888;">Printed from On Purpose Planner &mdash; onpurposeplanner.com</p>
  `;

  openPrintWindow(`Goals – ${monthNames[selectedMonth - 1]} ${selectedYear}`, html);
}

// ─────────────────────────────────────────────────────────────────────────────
// PRINT: Roles & Routines
// ─────────────────────────────────────────────────────────────────────────────
export async function printRoles(user) {
  const q        = query(
    collection(db, 'users', user.uid, 'roles'),
  );
  const snapshot = await getDocs(q);
  const roles    = snapshot.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999));

  if (roles.length === 0) {
    alert('No roles defined yet.');
    return;
  }

  const bands = [
    { key: 'morning',   label: 'Morning ☀' },
    { key: 'afternoon', label: 'Afternoon ◑' },
    { key: 'evening',   label: 'Evening ☽' },
  ];

  const rolesHtml = roles.map(role => {
    const bandsHtml = bands.map(b => `
      <div>
        <div class="band-label">${esc(b.label)}</div>
        <div class="band-text">${esc(role[b.key] || '—')}</div>
      </div>
    `).join('');

    const descHtml = role.description
      ? `<div class="role-description">${esc(role.description)}</div>`
      : '';

    return `
      <div class="role-card">
        <div class="role-title">${esc(role.title || 'Untitled Role')}</div>
        ${descHtml}
        <div class="bands-grid">${bandsHtml}</div>
      </div>
    `;
  }).join('');

  const html = `
    <div class="header">
      <h1>Roles &amp; Routines</h1>
      <p class="subtitle">Printed ${format(new Date(), 'MMMM d, yyyy')}</p>
    </div>
    ${rolesHtml}
    <p style="margin-top:18pt;font-size:9pt;color:#888;">Printed from On Purpose Planner &mdash; onpurposeplanner.com</p>
  `;

  openPrintWindow('Roles & Routines', html);
}

// ─────────────────────────────────────────────────────────────────────────────
// PRINT: Calendar (simple — prints what's visible on screen)
// ─────────────────────────────────────────────────────────────────────────────
export function printCalendar() {
  const html = `
    <div class="header">
      <h1>Calendar</h1>
      <p class="subtitle">Printed ${format(new Date(), 'MMMM d, yyyy')}</p>
    </div>
    <p class="section-content">Please use your browser's built-in print function (Ctrl+P / Cmd+P) for the full calendar view, or export directly from your calendar provider.</p>
    <p style="margin-top:18pt;font-size:9pt;color:#888;">Printed from On Purpose Planner &mdash; onpurposeplanner.com</p>
  `;
  openPrintWindow('Calendar', html);
}
